import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowUpRight,
  Menu,
  X,
  Copy,
  Check,
} from "lucide-react";
import Pagination from "../components/Pagination";
import ProfileAvatar from "../components/ProfileAvatar";
import MindshareFooter from "../components/MindshareFooter";
import MindshareNavbar from "../components/MindshareNavbar";
import { HERO_STATS } from "../constants/ui.constants";
import {
  useLeaderboard,
  useUserPerformance,
  useTableSort,
  useProfileInfo,
  useReferralStats,
  useReferralConnections,
  SORT_DIRECTION,
} from "../hooks";
import { useGlobalWallet } from "../context/WalletContext";
import { getQuests } from "../api/services/questService";
import apiClient from "../api/client";
import { ENDPOINTS } from "../api/endpoints";

const Mindshare = () => {
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [quests, setQuests] = useState([]);
  const [proposalsTotal, setProposalsTotal] = useState(HERO_STATS[2]?.value);
  const [topThreeContributors, setTopThreeContributors] = useState([]);
  const [copiedReferralCode, setCopiedReferralCode] = useState(false);

  // Get wallet connection status (MUST be first)
  const { address: walletAddress, isConnected, connect, formattedAddress, disconnect } = useGlobalWallet();

  // Fetch referral stats
  const {
    stats: referralStats,
    loading: referralLoading,
  } = useReferralStats(walletAddress, { immediate: !!walletAddress });

  // Fetch referral connections
  const {
    connections: referralConnections,
    loading: connectionsLoading,
  } = useReferralConnections(walletAddress, { immediate: !!walletAddress });

  // Fetch leaderboard data from API
  const {
    leaderboardData,
    totalItems,
    totalPages,
    currentPage,
    setPage,
    loading: leaderboardLoading,
    error: leaderboardError,
  } = useLeaderboard({
    limit: itemsPerPage,
  });

  // Setup table sorting
  const { sortedData, sortKey, direction, handleSort } = useTableSort(
    leaderboardData,
    "rank",
    SORT_DIRECTION.ASC,
  );

  // Fetch user performance data
  const {
    performanceData,
    loading: performanceLoading,
    error: performanceError,
    refetch: refetchPerformance,
  } = useUserPerformance(walletAddress, { immediate: !!walletAddress });

  // Handle Twitter OAuth callback
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    const error = searchParams.get("error");

    if (token) {
      localStorage.setItem("auth_token", token);
      window.history.replaceState({}, document.title, window.location.pathname);
      if (walletAddress) {
        refetchPerformance();
      }
    }

    if (error) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [walletAddress, refetchPerformance]);

  // Fetch user profile info
  const {
    profileData,
    loading: profileLoading,
    error: profileError,
  } = useProfileInfo(walletAddress, { immediate: !!walletAddress });

  // Sort indicator
  const SortIndicator = ({ columnKey }) => {
    if (sortKey !== columnKey)
      return <ArrowUpDown size={14} className="text-gray-500" />;
    return direction === SORT_DIRECTION.ASC ? (
      <ArrowUp size={14} className="text-[#D4AF37]" />
    ) : (
      <ArrowDown size={14} className="text-[#D4AF37]" />
    );
  };

  // Fetch proposals
  useEffect(() => {
    const fetchProposals = async () => {
      try {
        const data = await apiClient.get(ENDPOINTS.DAO.PROPOSALS);
        if (data.activeCount !== undefined) {
          setProposalsTotal(data.activeCount);
        }
      } catch (error) {
        console.error("Error fetching proposals:", error);
      }
    };
    fetchProposals();
  }, []);

  // Fetch top 3 contributors
  useEffect(() => {
    const fetchTopThree = async () => {
      try {
        const response = await apiClient.get(`${ENDPOINTS.USER.TOP}?page=1&limit=3`);
        const users = Array.isArray(response)
          ? response
          : response.data || response.users || [];
        
        const transformedUsers = users.map((user) => ({
          rank: user.rank || 0,
          name: user.twitterName || user.name || user.address,
          username: user.twitterUsername || user.username || user.address,
          handle: `@${user.twitterUsername || user.username || user.address}`,
          reach: user.twitterFollowers || user.followers || 0,
          score: user.score || 0,
          completedQuests: user.completedQuests || 0,
          followers: user.twitterFollowers || user.followers || 0,
          points: user.score || user.points || 0,
          verified: user.verified || false,
          twitterProfilePictureUrl: user.twitterProfilePictureUrl || user.profilePictureUrl || null,
        }));
        
        setTopThreeContributors(transformedUsers);
      } catch (error) {
        console.error("Error fetching top 3 contributors:", error);
      }
    };
    fetchTopThree();
  }, []);

  // Fetch quests (drives the "Active Quests" hero stat count)
  useEffect(() => {
    const fetchQuests = async () => {
      try {
        const data = await getQuests();
        setQuests(data);
      } catch (error) {
        console.error("Error fetching quests:", error);
      }
    };
    fetchQuests();
  }, []);

  const handlePageChange = (page) => {
    setPage(page);
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return "bg-[#D4AF37]";
    if (rank === 2) return "bg-gray-400";
    if (rank === 3) return "bg-[#CD7F32]";
    return "bg-white";
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div
      className="min-h-screen text-white"
      style={{
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#0A1628",
      }}
    >
      {/* Hero Section with Blue Background */}
      <div 
        className="relative"
        style={{
          backgroundImage: "url(/ggbrBackground.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Grid Lines Overlay */}
        <img 
          src="/gridlines.svg" 
          alt="" 
          className="absolute inset-0 w-full h-full z-[1] object-cover pointer-events-none"
        />

        {/* Radial Gradient Overlay */}
        <div 
          className="absolute inset-0 z-[2]"
          style={{
            background: "radial-gradient(28.06% 18.49% at 50% 50%, rgba(255, 255, 255, 0.5) 0%, rgba(50, 57, 104, 0.5) 100%)",
          }}
        />

        {/* Navigation Header */}
        <MindshareNavbar />

        {/* Hero Content - Aligned with navbar width */}
        <div className="relative z-[10] flex justify-center py-8 md:py-16 lg:py-20 px-4 md:px-0">
          <div className="w-full md:w-[91.5vw] relative">
            {/* Title - Centered */}
            <div className="text-center mb-8 md:mb-16">
              <h1
                className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl"
                style={{
                  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 400,
                  lineHeight: "1.1",
                }}
              >
                <span style={{ color: "#FFC966" }}>Goldfish</span>
                <br />
                <span style={{ color: "#FFFFFF" }}>Leaderboard</span>
              </h1>
            </div>

            {/* Content Grid: Left side (description + button) | Right side wide (2x2 stats) */}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start">
              {/* Left Side - Description and Button */}
              <div className="flex-shrink-0 flex flex-col w-full lg:max-w-[520px]">
                <div className="mb-4">
                  <p
                    className="text-base sm:text-lg md:text-xl lg:text-[25px]"
                    style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "140%", color: "rgba(255, 255, 255, 0.7)", marginBottom: "0" }}
                  >
                    The Goldfish Leaderboard is built to surface, score, and reward real contributions across the Goldfish ecosystem, using points tied to the governance token $GFIN.
                  </p>
                </div>

                {/* Referral Link - Shows when logged in */}
                {isConnected && (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center gap-3">
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}?ref=${referralStats?.referralCode || walletAddress?.slice(0, 8).toLowerCase() || 'loading'}`}
                        placeholder="Loading referral code..."
                        style={{
                          background: "rgba(26, 42, 74, 0.5)",
                          color: "white",
                          padding: "10px 12px",
                          borderRadius: "4px",
                          border: "1px solid rgba(212, 175, 55, 0.3)",
                          fontSize: "13px",
                          flex: 1,
                        }}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}?ref=${referralStats?.referralCode || walletAddress?.slice(0, 8).toLowerCase()}`);
                          setCopiedReferralCode(true);
                          setTimeout(() => setCopiedReferralCode(false), 2000);
                        }}
                        style={{
                          background: "#D4AF37",
                          color: "#1B2464",
                          padding: "10px 12px",
                          borderRadius: "4px",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontWeight: "600",
                          fontSize: "13px",
                        }}
                      >
                        {copiedReferralCode ? (
                          <><Check size={16} /> Copied</>
                        ) : (
                          <><Copy size={16} /> Copy</>
                        )}
                      </button>
                    </div>
                    {/* Referral Count Debug Display */}
                    <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)" }}>
                      Successful referrals: <span style={{ color: "#D4AF37", fontWeight: "600" }}>{referralConnections?.totalReferredCount || 0}</span>
                      {connectionsLoading && <span> (loading...)</span>}
                      {referralLoading && <span> (loading...)</span>}
                    </div>
                    {/* Referral Info Text */}
                    <div style={{ fontSize: "14px", color: "rgba(255, 255, 255, 0.7)", marginTop: "12px", lineHeight: "1.5" }}>
                      Invite your friends with your custom referral link and get 5% of all points they earn
                    </div>
                  </div>
                )}

                {isConnected ? (
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button 
                      disabled
                      className="px-4 md:px-6 py-3 md:py-4 rounded text-sm md:text-base font-semibold transition-all"
                      style={{ 
                        background: "#4A5A8A",
                        border: "1px solid #4A5A8A",
                        color: "#FFFFFF",
                        width: "fit-content",
                      }}
                    >
                      {formattedAddress}
                    </button>
                    <button 
                      onClick={disconnect}
                      className="px-4 md:px-6 py-3 md:py-4 rounded text-sm md:text-base font-semibold transition-all"
                      style={{ 
                        background: "transparent",
                        border: "1px solid #FFC966",
                        color: "#FFC966",
                        width: "fit-content",
                      }}
                    >
                      DISCONNECT
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={connect}
                    className="px-4 md:px-6 py-3 md:py-4 rounded text-sm md:text-base font-semibold transition-all"
                    style={{ 
                      background: "#FFC966",
                      border: "1px solid #FFC966",
                      color: "#000000",
                      width: "fit-content",
                    }}
                  >
                    CONNECT WALLET
                  </button>
                )}
              </div>

              {/* Right Side - 2x2 Stats Grid (Takes up remaining space) */}
              <div className="flex-1 grid grid-cols-2 gap-3 md:gap-4 lg:gap-6 lg:pl-8 xl:pl-12">
                {/* Reward Pool */}
                <div className="flex items-start gap-2 md:gap-3">
                  <img src="/dollar-sign.svg" alt="Reward" className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 flex-shrink-0" />
                  <div className="flex flex-col justify-start flex-1 min-h-fit">
                    <div className="text-base sm:text-lg md:text-2xl lg:text-4xl xl:text-[56px]" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-2%", color: "#D4AF37" }}>
                      Largest
                    </div>
                    <div className="text-[9px] sm:text-xs md:text-sm lg:text-base xl:text-lg" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-1%", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase" }}>
                      $GFIN
                    </div>
                    <div className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm" style={{ fontFamily: "Inter", fontWeight: 400, color: "rgba(255, 255, 255, 0.5)", textTransform: "uppercase", marginTop: "2px" }}>
                      Pool
                    </div>
                  </div>
                </div>

                {/* DAO Members */}
                <div className="flex items-start gap-2 md:gap-3">
                  <img src="/users-round.svg" alt="Members" className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 flex-shrink-0" />
                  <div className="flex flex-col justify-start flex-1 min-h-fit">
                    <div className="text-base sm:text-lg md:text-2xl lg:text-4xl xl:text-[56px]" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-2%", color: "#D4AF37" }}>
                      1.8K
                    </div>
                    <div className="text-[9px] sm:text-xs md:text-sm lg:text-base xl:text-lg" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-1%", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase" }}>
                      DAO
                    </div>
                    <div className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm" style={{ fontFamily: "Inter", fontWeight: 400, color: "rgba(255, 255, 255, 0.5)", textTransform: "uppercase", marginTop: "2px" }}>
                      Members
                    </div>
                  </div>
                </div>

                {/* Active Proposals */}
                <div className="flex items-start gap-2 md:gap-3">
                  <img src="/gem.svg" alt="Proposals" className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 flex-shrink-0" />
                  <div className="flex flex-col justify-start flex-1 min-h-fit">
                    <div className="text-base sm:text-lg md:text-2xl lg:text-4xl xl:text-[56px]" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-2%", color: "#D4AF37" }}>
                      {proposalsTotal}
                    </div>
                    <div className="text-[9px] sm:text-xs md:text-sm lg:text-base xl:text-lg" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-1%", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase" }}>
                      Active
                    </div>
                    <div className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm" style={{ fontFamily: "Inter", fontWeight: 400, color: "rgba(255, 255, 255, 0.5)", textTransform: "uppercase", marginTop: "2px" }}>
                      Proposals
                    </div>
                  </div>
                </div>

                {/* Active Quests */}
                <div className="flex items-start gap-2 md:gap-3">
                  <img src="/git-pull-request-create-arrow.svg" alt="Quests" className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 flex-shrink-0" />
                  <div className="flex flex-col justify-start flex-1 min-h-fit">
                    <div className="text-base sm:text-lg md:text-2xl lg:text-4xl xl:text-[56px]" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-2%", color: "#D4AF37" }}>
                      {quests.length || 6}
                    </div>
                    <div className="text-[9px] sm:text-xs md:text-sm lg:text-base xl:text-lg" style={{ fontFamily: "Inter", fontWeight: 400, lineHeight: "1.1", letterSpacing: "-1%", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase" }}>
                      Active
                    </div>
                    <div className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm" style={{ fontFamily: "Inter", fontWeight: 400, color: "rgba(255, 255, 255, 0.5)", textTransform: "uppercase", marginTop: "2px" }}>
                      Quests
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Discord Promotional Section */}
        <div className="relative z-[10] flex justify-center py-12 md:py-16 px-4 md:px-0">
          <div className="w-full md:w-[85vw] relative flex flex-col md:flex-row items-center justify-between gap-8 md:gap-16">
            {/* Left Side - Slanted blue card with Discord logo */}
            <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: "100%", maxWidth: "500px", height: "420px", perspective: "1000px" }}>
              <svg
                width="450"
                height="450"
                viewBox="0 0 450 450"
                style={{
                  transform: "rotateY(15deg) rotateX(-5deg)",
                  transformStyle: "preserve-3d",
                  filter: "drop-shadow(20px 10px 40px rgba(0, 0, 0, 0.4))",
                }}
              >
                <defs>
                  <linearGradient id="shineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: "rgba(255,255,255,0.2)", stopOpacity: 1 }} />
                    <stop offset="40%" style={{ stopColor: "rgba(255,255,255,0)", stopOpacity: 0 }} />
                  </linearGradient>
                  <filter id="innerShadow">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                    <feOffset dx="2" dy="2" result="offsetblur" />
                    <feFlood floodColor="rgba(255,255,255,0.3)" />
                    <feComposite in2="offsetblur" operator="in" />
                    <feMerge>
                      <feMergeNode />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Main slanted card shape with rounded corners and 1px black border */}
                <path
                  d="M 20 80 Q 0 80 0 100 L 0 430 Q 0 450 20 450 L 430 370 Q 450 370 450 350 L 450 20 Q 450 0 430 0 L 40 80 Q 20 80 20 80 Z"
                  fill="#212C7E"
                  stroke="#000000"
                  strokeWidth="1"
                  filter="url(#innerShadow)"
                />
                {/* Shine overlay */}
                <path
                  d="M 20 80 Q 0 80 0 100 L 0 430 Q 0 450 20 450 L 430 370 Q 450 370 450 350 L 450 20 Q 450 0 430 0 L 40 80 Q 20 80 20 80 Z"
                  fill="url(#shineGradient)"
                />
                {/* Top-right fold effect */}
                <path
                  d="M 420 15 L 435 0 L 450 20 Z"
                  fill="rgba(0, 0, 0, 0.3)"
                />
              </svg>
              {/* Discord logo positioned absolutely on top */}
              <img
                src="/discordYellow.png"
                alt="Discord"
                className="absolute w-[160px] md:w-[190px] h-auto object-contain"
                style={{ 
                  top: "50%", 
                  left: "50%", 
                  transform: "translate(-50%, -50%) rotateY(15deg) rotateX(-5deg)",
                  transformStyle: "preserve-3d",
                  zIndex: 10
                }}
              />
            </div>

            {/* Right Side - Text with corner brackets */}
            <div className="flex flex-col items-end gap-6 md:gap-8">

              {/* Main Text with Corner Brackets */}
              <div
                className="relative p-6 md:p-8"
                style={{
                  maxWidth: "600px"
                }}
              >
                {/* Top-right corner bracket */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "60px",
                    height: "60px",
                    borderTop: "2px solid #DBA339",
                    borderRight: "2px solid #DBA339",
                  }}
                />
                {/* Bottom-left corner bracket */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "60px",
                    height: "60px",
                    borderBottom: "2px solid #DBA339",
                    borderLeft: "2px solid #DBA339",
                  }}
                />
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight text-center md:text-right whitespace-nowrap"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 400,
                    color: "#DBA339",
                  }}
                >
                  Join Goldfish Discord<br />to Earn 100 Bonus Points
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Top Contributors Section - Inside Hero */}
        <div className="relative z-[10] flex justify-center py-12 md:py-16 px-4 md:px-0">
          {/* Full width container aligned with navbar */}
          <div className="w-full md:w-[85vw] relative flex flex-col lg:flex-row items-start lg:items-end gap-6 lg:gap-8 lg:justify-between">
            {/* Header - Left Side */}
            <div className="lg:flex-shrink-0">
              <h2 className="text-4xl md:text-5xl lg:text-7xl" style={{ fontFamily: "Inter", fontWeight: "400", color: "white", lineHeight: "94%", letterSpacing: "-5%" }}>
                Top<br />Contributors
              </h2>
            </div>

            {/* Cards Container - Right side */}
            <div className="w-full flex flex-col sm:flex-row items-center sm:items-end gap-2 sm:gap-3 md:gap-4 justify-center lg:justify-end">
                {/* 2nd Place - Left */}
                {topThreeContributors[1] && (
                  <div className="flex flex-col items-center order-2 md:order-1" style={{ background: "rgba(26, 42, 74, 0.9)", width: "240px", minHeight: "450px" }}>
                    {/* Top Section with Gradient - Includes Metrics */}
                    <div className="w-full p-4" style={{ background: "linear-gradient(134.45deg, rgba(63, 81, 150, 0.8) 0.47%, rgba(27, 36, 100, 0.8) 99.53%)", backdropFilter: "blur(5px)" }}>
                      {/* Avatar */}
                      <div className="mb-5 flex justify-center">
                        <img src={topThreeContributors[1].twitterProfilePictureUrl?.replace('_normal', '_400x400')} alt={topThreeContributors[1].username} className="w-40 h-32 object-cover" />
                      </div>
                      {/* Twitter Handle */}
                      <div className="text-white font-semibold text-sm mb-4 text-center">{topThreeContributors[1].handle}</div>
                      {/* Stats Section inside gradient */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/arrow-up-narrow-wide.svg" alt="rank" className="w-4 h-4" style={{ filter: "brightness(0) invert(1)" }} />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "12px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Rank</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[1].rank}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/star.svg" alt="points" className="w-4 h-4" />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "12px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Points</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[1].points?.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/users-round.svg" alt="followers" className="w-4 h-4" style={{ filter: "brightness(0) invert(1)" }} />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "12px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Followers</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[1].reach}</span>
                        </div>
                      </div>
                    </div>
                    {/* Trophy Section with Gold Gradient */}
                    <div className="w-full flex-1 flex justify-center items-center relative overflow-hidden" style={{ backgroundImage: "url('/Numbers element.svg')", backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }}>
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(255, 201, 102, 0.4) 0%, rgba(26, 42, 74, 0) 70%)" }}></div>
                      <img src="/trophy2.svg" alt="2nd" className="w-16 h-16 relative z-20" />
                    </div>
                  </div>
                )}

                {/* 1st Place - Center */}
                {topThreeContributors[0] && (
                  <div className="flex flex-col items-center order-1 md:order-2" style={{ background: "rgba(26, 42, 74, 0.9)", width: "260px", minHeight: "480px" }}>
                    {/* Top Section with Gradient - Includes Metrics */}
                    <div className="w-full p-4" style={{ background: "linear-gradient(134.45deg, rgba(63, 81, 150, 0.8) 0.47%, rgba(27, 36, 100, 0.8) 99.53%)", backdropFilter: "blur(5px)" }}>
                      {/* Avatar */}
                      <div className="mb-5 flex justify-center">
                        <img src={topThreeContributors[0].twitterProfilePictureUrl?.replace('_normal', '_400x400')} alt={topThreeContributors[0].username} className="w-44 h-36 object-cover" />
                      </div>
                      {/* Twitter Handle */}
                      <div className="text-white font-semibold text-sm mb-4 text-center">{topThreeContributors[0].handle}</div>
                      {/* Stats Section inside gradient */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/arrow-up-narrow-wide.svg" alt="rank" className="w-4 h-4" style={{ filter: "brightness(0) invert(1)" }} />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Current Rank</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[0].rank}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/star.svg" alt="points" className="w-4 h-4" />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Points</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[0].points?.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/users-round.svg" alt="followers" className="w-4 h-4" style={{ filter: "brightness(0) invert(1)" }} />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Followers</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[0].reach}</span>
                        </div>
                      </div>
                    </div>
                    {/* Trophy Section with Gold Gradient */}
                    <div className="w-full flex-1 flex justify-center items-center relative overflow-hidden" style={{ backgroundImage: "url('/Numbers element.svg')", backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }}>
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(255, 201, 102, 0.4) 0%, rgba(26, 42, 74, 0) 70%)" }}></div>
                      <img src="/trophy1.svg" alt="1st" className="w-20 h-20 relative z-20" />
                    </div>
                  </div>
                )}

                {/* 3rd Place - Right */}
                {topThreeContributors[2] && (
                  <div className="flex flex-col items-center order-3" style={{ background: "rgba(26, 42, 74, 0.9)", width: "240px", minHeight: "450px" }}>
                    {/* Top Section with Gradient - Includes Metrics */}
                    <div className="w-full p-4" style={{ background: "linear-gradient(134.45deg, rgba(63, 81, 150, 0.8) 0.47%, rgba(27, 36, 100, 0.8) 99.53%)", backdropFilter: "blur(5px)" }}>
                      {/* Avatar */}
                      <div className="mb-5 flex justify-center">
                        <img src={topThreeContributors[2].twitterProfilePictureUrl?.replace('_normal', '_400x400')} alt={topThreeContributors[2].username} className="w-40 h-32 object-cover" />
                      </div>
                      {/* Twitter Handle */}
                      <div className="text-white font-semibold text-sm mb-4 text-center">{topThreeContributors[2].handle}</div>
                      {/* Stats Section inside gradient */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/arrow-up-narrow-wide.svg" alt="rank" className="w-4 h-4" style={{ filter: "brightness(0) invert(1)" }} />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Current Rank</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[2].rank}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/star.svg" alt="points" className="w-4 h-4" />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Points</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[2].points?.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img src="/users-round.svg" alt="followers" className="w-4 h-4" style={{ filter: "brightness(0) invert(1)" }} />
                            <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>Followers</span>
                          </div>
                          <span style={{ fontFamily: "Inter", fontWeight: 400, fontSize: "18px", lineHeight: "83%", letterSpacing: "-5%", color: "#FFFFFF" }}>{topThreeContributors[2].reach}</span>
                        </div>
                      </div>
                    </div>
                    {/* Trophy Section with Gold Gradient */}
                    <div className="w-full flex-1 flex justify-center items-center relative overflow-hidden" style={{ backgroundImage: "url('/Numbers element.svg')", backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }}>
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(255, 201, 102, 0.4) 0%, rgba(26, 42, 74, 0) 70%)" }}></div>
                      <img src="/trophy3.svg" alt="3rd" className="w-16 h-16 relative z-20" />
                    </div>
                  </div>
                )}
            </div>
            {/* end cards container */}
          </div>
          {/* end top contributors container */}
        </div>

      </div>

      {/* Golden Ticket Conversion Section */}
      <div 
        className="py-12 md:py-20 flex justify-center px-4 md:px-0"
        style={{ 
          background: "#FFFFFF",
          borderTop: "3px solid #d4af37",
          borderBottom: "3px solid #d4af37"
        }}
      >
        <div className="w-full md:w-[85vw]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Side - Content */}
            <div 
              className="flex flex-col p-6 md:p-8"
              style={{
                gap: window.innerWidth < 768 ? "20px" : "30px"
              }}
            >
              {/* Top section - Badge and Title */}
              <div style={{ display: "flex", flexDirection: "column", gap: window.innerWidth < 768 ? "15px" : "20px" }}>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 300,
                  color: "#c49b33",
                  lineHeight: "1.1",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)"
                }}
              >
                $GGBR now available<br />on MEXC
              </h1>
              </div>
              
              {/* Middle section - Description */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <p 
                className="text-base md:text-lg"
                style={{
                  lineHeight: "1.6",
                  color: "#333",
                  maxWidth: "650px",
                  margin: 0
                }}
              >
                Trade gold-backed value on-chain, directly from one of the world's most liquid exchanges.
              </p>
              </div>
              {/* Bottom section - Button */}
              <a
                href="https://www.mexc.com/en-GB/exchange/GGBR_USDT"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all"
                style={{
                  background: "#1e1548",
                  color: "#DBA339",
                  padding: "12px 28px",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  width: "fit-content",
                  boxShadow: "0 4px 12px rgba(30, 21, 72, 0.3)",
                  fontFamily: "'Playfair Display', Georgia, serif",
                  textDecoration: "none",
                  display: "inline-block"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#2d1f6b";
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 16px rgba(30, 21, 72, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#1e1548";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 12px rgba(30, 21, 72, 0.3)";
                }}
              >
                BUY $GGBR ON MEXC
              </a>
            </div>
            
            {/* Right Side - Laptop with MEXC */}
            <div className="flex justify-center items-center mt-8 lg:mt-0" style={{ overflow: "visible" }}>
              <div 
                className="relative h-[300px] md:h-[550px]"
                style={{
                  width: "100%",
                  maxWidth: "700px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "visible"
                }}
              >
                {/* Matrix Background - Faded */}
                <div 
                  className="absolute inset-0 flex justify-center items-center"
                  style={{ zIndex: 0 }}
                >
                  <img
                    src="/matrix.png"
                    alt="Matrix Background"
                    className="w-full h-full object-cover"
                    style={{ 
                      opacity: 0.15,
                      filter: "blur(1px)"
                    }}
                  />
                </div>

                {/* Laptop Image */}
                <div 
                  className="absolute inset-0 flex justify-center items-center"
                  style={{ zIndex: 2 }}
                >
                  <img
                    src="/laptop.png"
                    alt="Laptop"
                    className="w-full h-auto object-contain"
                    style={{ 
                      maxWidth: "85%",
                      filter: "drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3))"
                    }}
                  />
                </div>
                
                {/* Background Ellipse - Under Laptop */}
                <div 
                  className="absolute inset-0 flex justify-center items-center"
                  style={{ zIndex: 1 }}
                >
                  <img
                    src="/Ellipse 3.png"
                    alt="Background"
                    className="w-full h-auto object-contain"
                    style={{ 
                      maxWidth: "60%",
                      opacity: 0.9,
                      transform: "translateX(10%)"
                    }}
                  />
                </div>
                
                {/* MEXC Icon - On the left border of the ellipse */}
                <div 
                  className="absolute"
                  style={{ 
                    zIndex: 3,
                    top: "55%",
                    left: "18%",
                    transform: "translateY(-50%)"
                  }}
                >
                  <img
                    src="/mexcicon.png"
                    alt="MEXC"
                    className="w-16 h-16 md:w-24 md:h-24 object-contain"
                    style={{ 
                      filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))",
                      animation: "float 3s ease-in-out infinite"
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Users Section */}
      <div
        className="py-8 md:py-16 flex justify-center px-4 md:px-0"
        style={{ background: "#FFFFFF" }}
      >
        <div className="w-full md:w-[85vw]">
          <h2 
            className="text-2xl md:text-4xl lg:text-5xl mb-4 md:mb-8"
            style={{ color: "#000000", fontWeight: 400 }}
          >
            Table <span style={{ color: "#D4AF37" }}>of users</span>
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
            {/* Table */}
            <div className="lg:col-span-2">
              <div 
                className="overflow-x-auto"
                style={{ background: "#F9F9F9" }}
              >
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr style={{ background: "#1A2E5A" }}>
                      <th className="text-left py-2 px-3 md:px-6 text-xs md:text-sm font-medium" style={{ color: "#FFFFFF" }}>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("rank")}>
                          Rank <SortIndicator columnKey="rank" />
                        </div>
                      </th>
                      <th className="text-left py-2 px-3 md:px-6 text-xs md:text-sm font-medium" style={{ color: "#FFFFFF" }}>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("username")}>
                          User <SortIndicator columnKey="username" />
                        </div>
                      </th>
                      <th className="text-left py-2 px-3 md:px-6 text-xs md:text-sm font-medium hidden sm:table-cell" style={{ color: "#FFFFFF" }}>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("mindshare")}>
                          Mindshare <SortIndicator columnKey="mindshare" />
                        </div>
                      </th>
                      <th className="text-left py-2 px-3 md:px-6 text-xs md:text-sm font-medium hidden md:table-cell" style={{ color: "#FFFFFF" }}>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("reach")}>
                          Followers <SortIndicator columnKey="reach" />
                        </div>
                      </th>
                      <th className="text-left py-2 px-3 md:px-6 text-xs md:text-sm font-medium hidden lg:table-cell" style={{ color: "#FFFFFF" }}>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("xpScore")}>
                          Quest <SortIndicator columnKey="xpScore" />
                        </div>
                      </th>
                      <th className="text-left py-2 px-3 md:px-6 text-xs md:text-sm font-medium" style={{ color: "#FFFFFF" }}>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("totalScore")}>
                          Score <SortIndicator columnKey="totalScore" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(sortedData) && sortedData.length > 0 ? (
                      sortedData.map((user, index) => (
                        <tr
                          key={index}
                          style={{ 
                            borderBottom: "1px solid #E8E8E8",
                            background: "transparent"
                          }}
                        >
                          <td className="py-3 md:py-4 px-3 md:px-6">
                            <div 
                              className={`${getRankBadgeColor(user.rank)} w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-xs md:text-sm font-bold`}
                              style={{ color: user.rank <= 3 ? "#FFFFFF" : "#1A2E5A" }}
                            >
                              {user.rank}
                            </div>
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6">
                            <div className="flex items-center gap-2">
                              <ProfileAvatar
                                src={user.twitterProfilePictureUrl}
                                alt={user.username}
                                className="w-6 h-6 md:w-8 md:h-8"
                              />
                              <div>
                                <div className="text-gray-900 text-xs md:text-sm font-medium">{user.name}</div>
                                <div className="text-xs hidden sm:block" style={{ color: "rgba(0,0,0,0.5)" }}>{user.handle}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 text-gray-900 text-xs md:text-sm hidden sm:table-cell">
                            <span style={{ background: "#FDE8C3", padding: "2px 6px" }}>{user.mindshareFormatted}</span>
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 text-gray-900 text-xs md:text-sm hidden md:table-cell">
                            <span style={{ background: "#FDE8C3", padding: "2px 6px" }}>{user.reachFormatted}</span>
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 text-gray-900 text-xs md:text-sm hidden lg:table-cell">
                            <span style={{ background: "#FDE8C3", padding: "2px 6px" }}>{user.xpScoreFormatted}</span>
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 text-gray-900 text-xs md:text-sm">
                            <span style={{ background: "#FDE8C3", padding: "2px 6px" }}>{user.totalScoreFormatted}</span>
                          </td>
                        </tr>
                      ))
                    ) : leaderboardError ? (
                      <tr>
                        <td colSpan="6" className="text-center py-12 text-red-400">
                          Unable to load leaderboard
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-12 text-gray-400">
                          {leaderboardLoading ? "Loading leaderboard..." : "No data available"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination - Outside Table */}
              <div className="px-6 py-3" style={{ background: "#FFFFFF", color: "#000000", borderTop: "1px solid #E8E8E8" }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  isLoading={leaderboardLoading}
                />
              </div>
            </div>

            {/* Profile Card */}
            <div className="lg:col-span-1 flex flex-col">
              <div 
                className="h-auto md:h-[540px]"
                style={{ background: "#FFFFFF", padding: "5px", border: "0.5px solid #B7B7B7", display: "flex", flexDirection: "column" }}
              >
                <div 
                  className="p-4 md:p-6"
                  style={{ background: "linear-gradient(134.45deg, rgba(248, 248, 248, 0.3) 0.47%, rgba(226, 226, 226, 0.3) 99.53%)", backdropFilter: "blur(10.3px)", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
                >
                  {/* Selected User Profile - Show connected user if available, otherwise top contributor */}
                  {(profileData || topThreeContributors[0]) && (
                    <>
                      <div className="flex justify-center w-full overflow-visible mb-3">
                        <div className="w-[180px] h-[140px] md:w-[280px] md:h-[220px] overflow-hidden">
                          <img src={profileData?.twitterProfilePictureUrl?.replace('_normal', '_400x400') || topThreeContributors[0]?.twitterProfilePictureUrl} alt={profileData?.twitterName || topThreeContributors[0]?.username} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-lg md:text-[26px]" style={{ color: "#1B2464" }}>{profileData?.twitterName || topThreeContributors[0]?.handle}</div>
                      </div>
                      <div className="space-y-2 flex-1 flex flex-col justify-center">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 md:gap-3">
                            <img src="/arrow-up-narrow-wide.svg" alt="rank" className="w-4 h-4 md:w-5 md:h-5" style={{ filter: "brightness(0) saturate(100%) invert(20%) sepia(70%) saturate(400%) hue-rotate(200deg)" }} />
                            <span className="text-sm md:text-base" style={{ color: "#1B2464", fontWeight: "500" }}>Current Rank</span>
                          </div>
                          <span className="text-lg md:text-2xl" style={{ color: "#1B2464", fontWeight: "600" }}>{profileData?.rank || topThreeContributors[0]?.rank}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 md:gap-3">
                            <img src="/git-pull-request-create-arrow.svg" alt="quests" className="w-4 h-4 md:w-5 md:h-5" style={{ filter: "brightness(0) saturate(100%) invert(20%) sepia(70%) saturate(400%) hue-rotate(200deg)" }} />
                            <span className="text-sm md:text-base" style={{ color: "#1B2464", fontWeight: "500" }}>Mindshare</span>
                          </div>
                          <span className="text-lg md:text-2xl" style={{ color: "#1B2464", fontWeight: "600" }}>{typeof (profileData?.mindshare || topThreeContributors[0]?.mindshare) === 'number' ? (profileData?.mindshare || topThreeContributors[0]?.mindshare).toFixed(2) : (profileData?.mindshare || topThreeContributors[0]?.mindshare || '0.00')}%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 md:gap-3">
                            <img src="/star.svg" alt="points" className="w-4 h-4 md:w-5 md:h-5" style={{ filter: "brightness(0) saturate(100%) invert(20%) sepia(70%) saturate(400%) hue-rotate(200deg)" }} />
                            <span className="text-sm md:text-base" style={{ color: "#1B2464", fontWeight: "500" }}>Points</span>
                          </div>
                          <span className="text-lg md:text-2xl" style={{ color: "#1B2464", fontWeight: "600" }}>{typeof (profileData?.score || topThreeContributors[0]?.points) === 'number' ? (profileData?.score || topThreeContributors[0]?.points).toFixed(2) : (profileData?.score || topThreeContributors[0]?.points || '0.00')}</span>
                        </div>
                      </div>
                      <button 
                        className="w-full font-medium flex items-center justify-center gap-2"
                        style={{ background: "#1B2464", color: "#FFFFFF", padding: "12px", fontSize: "14px", letterSpacing: "0.5px" }}
                        onClick={() => navigate("/profile")}
                      >
                        VIEW PROFILE <ArrowUpRight size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MindshareFooter />
    </div>
  );
};

export default Mindshare;
